import { useState, useMemo, useRef, useEffect } from 'react';
import { trackEvent } from '@/lib/trackEvent';
import { Send, Loader2, Lock } from 'lucide-react';
import HelpTip from '../HelpTip';
import { SmsConsentDisclosure, SMS_CONSENT_VERSION } from '@/components/forms/SmsConsentDisclosure';
import { Checkbox } from '@/components/ui/checkbox';
import { calculateEstimate } from '@shared/calculateEstimate';
import { useWidgetState } from '../useWidgetState';
import { eff, stepTitleStyle, stepSubtitleStyle, inputStyle, primaryButtonStyle } from '../designTokens';
import type { StepDefinition } from '@shared/wizardSchema';

interface LeadCaptureStepProps {
  step: StepDefinition;
  accentColor?: string;
}

type CustomFieldType = 'text' | 'email' | 'phone' | 'number' | 'select' | 'textarea' | 'checkbox';

interface CustomField {
  id: string;
  type: CustomFieldType;
  label: string;
  required: boolean;
  options: string[];
}

/**
 * Collects lead information and submits to POST /api/leads.
 * Reads/writes through widget state (leadData, smsConsent, leadSubmitted).
 * Does NOT handle coupons or expiration — those are separate concerns.
 */
export default function LeadCaptureStep({ step, accentColor }: LeadCaptureStepProps) {
  const {
    config,
    state,
    dispatch,
    leadData,
    leadSubmitted,
    updateLead,
    estimateInputs,
    answers,
    nextStep,
    prevStep,
  } = useWidgetState();

  // Derive current estimate for quote_amount (same pattern as PriceRevealStep)
  const estimate = useMemo(
    () => calculateEstimate(config.pricingConfig, estimateInputs),
    [config.pricingConfig, estimateInputs],
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; phone?: string }>({});
  // Ref-based guard prevents double-submit across re-renders
  const submitLockRef = useRef(false);

  // Owner-defined custom lead fields (calculator_settings.lead_form.custom_fields).
  const customFields = useMemo<CustomField[]>(() => {
    const cs = config.calculator.calculator_settings as any;
    const cf = cs?.lead_form?.custom_fields;
    return Array.isArray(cf) ? cf : [];
  }, [config.calculator.calculator_settings]);
  const [customValues, setCustomValues] = useState<Record<string, string | boolean>>({});
  const [customErrors, setCustomErrors] = useState<Record<string, string>>({});

  const setCustomValue = (id: string, value: string | boolean) => {
    setCustomValues((p) => ({ ...p, [id]: value }));
    setCustomErrors((p) => ({ ...p, [id]: '' }));
    setError(null);
  };

  const smsConsent = state.lead.smsConsent;

  function validateFields(): boolean {
    const email = leadData.email.trim();
    const phone = leadData.phone.trim();
    const errs: { email?: string; phone?: string } = {};

    // Require at least email or phone
    if (!email && !phone) {
      setError('Please provide your email or phone number.');
      return false;
    }

    // Validate email format if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address.';
    }

    // Validate phone has at least 7 digits if provided
    if (phone && phone.replace(/\D/g, '').length < 7) {
      errs.phone = 'Please enter a valid phone number.';
    }

    // Required custom fields
    const cErrs: Record<string, string> = {};
    for (const f of customFields) {
      if (!f.required) continue;
      const v = customValues[f.id];
      const empty = f.type === 'checkbox' ? v !== true : !String(v ?? '').trim();
      if (empty) cErrs[f.id] = `${f.label || 'This field'} is required.`;
    }
    setCustomErrors(cErrs);

    setFieldErrors(errs);
    if (Object.keys(errs).length > 0 || Object.keys(cErrs).length > 0) return false;

    setError(null);
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || leadSubmitted || submitLockRef.current) return;

    if (!validateFields()) return;

    submitLockRef.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const isDemo = config.calculator.id === 0;
      const isPreview = config.calculator.id < 0;

      // Preview mode: skip API call, just advance
      if (isPreview) {
        dispatch({ type: 'MARK_LEAD_SUBMITTED' });
        nextStep();
        return;
      }

      // Ensure answers is always a non-null object (cloned — never mutate state).
      const safeAnswers: Record<string, any> = { ...((answers && typeof answers === 'object') ? answers : {}) };
      // Merge owner-defined custom field values, keyed by their label.
      for (const f of customFields) {
        const v = customValues[f.id];
        if (v !== undefined && v !== '' && v !== false) {
          safeAnswers[f.label || f.id] = v;
        }
      }

      // Ensure quote_amount is a finite number or null
      const rawTotal = estimate?.total;
      const safeQuoteAmount = (rawTotal != null && Number.isFinite(rawTotal)) ? rawTotal : null;

      const endpoint = isDemo ? '/api/demo-leads' : '/api/leads';
      const body = isDemo
        ? {
            email: leadData.email.trim() || null,
            name: leadData.name.trim() || null,
            phone: leadData.phone.trim() || null,
            company: leadData.company.trim() || null,
            trade: (config.calculator.slug || '').replace('demo-', ''),
            demoBusinessName: config.calculator.business_name || null,
            quoteAmount: safeQuoteAmount,
            answers: safeAnswers,
            smsConsent: smsConsent,
            source_tool: "demo",
            source_page: typeof window !== "undefined" ? window.location.pathname : null,
          }
        : (() => {
            // Capture UTM / source attribution from the current page
            const params = new URLSearchParams(window.location.search);
            return {
              calculator_id: config.calculator.id,
              name: leadData.name.trim() || null,
              email: leadData.email.trim() || null,
              phone: leadData.phone.trim() || null,
              company: leadData.company.trim() || null,
              quote_amount: safeQuoteAmount,
              answers: safeAnswers,
              sms_consent: smsConsent,
              consent_timestamp: smsConsent ? new Date().toISOString() : null,
              consent_text_version: smsConsent ? SMS_CONSENT_VERSION : null,
              landing_page: window.location.href || null,
              referrer: document.referrer || null,
              utm_source: params.get('utm_source') || null,
              utm_medium: params.get('utm_medium') || null,
              utm_campaign: params.get('utm_campaign') || null,
            };
          })();

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify(body),
      });
      clearTimeout(timeout);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }

      dispatch({ type: 'MARK_LEAD_SUBMITTED' });
      if (config.calculator.id === 0) {
        trackEvent("demo_lead_submitted", { trade: (config.calculator.slug || "").replace("demo-", ""), quoteAmount: safeQuoteAmount });
      }
      // Advance to next step (booking or confirmation)
      nextStep();
    } catch (err) {
      // Allow retry on failure
      submitLockRef.current = false;
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Already submitted: auto-advance if user navigates back here ───
  useEffect(() => {
    if (leadSubmitted) nextStep();
  }, [leadSubmitted, nextStep]);

  // ─── Form ───
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        {/* Wave R-pre v2 — back button at top of the lead-capture step.
         *  Previously the user could only go forward; no way back to the
         *  calculator. Uses prevStep from useWidgetState which knows the
         *  flow's previous visible step. */}
        <button
          type="button"
          onClick={prevStep}
          data-testid="lead-capture-back"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', marginBottom: 12,
            background: 'transparent',
            color: eff.textBody,
            border: 'none',
            fontSize: '13px', fontWeight: 600,
            cursor: 'pointer',
            borderRadius: 6,
          }}
          aria-label="Back"
        >
          <span aria-hidden="true">←</span> Back
        </button>
        <h3 style={stepTitleStyle}>Where should we send your quote?</h3>
        <p style={{ ...stepSubtitleStyle, margin: '6px 0 0' }}>
          We'll email you a detailed breakdown. No account needed.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label htmlFor="lead-email" style={{ fontSize: '13px', fontWeight: 600, color: eff.text, display: 'block', marginBottom: '6px' }}>
            Email
          </label>
          <input
            id="lead-email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            value={leadData.email}
            onChange={(e) => { updateLead('email', e.target.value); setFieldErrors(p => ({ ...p, email: undefined })); setError(null); }}
            style={{
              ...inputStyle,
              ...(fieldErrors.email ? { borderColor: eff.error } : {}),
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = fieldErrors.email ? eff.error : eff.buttonBg; e.currentTarget.style.boxShadow = `0 0 0 3px ${eff.buttonBorder}`; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = fieldErrors.email ? eff.error : eff.buttonBorder; e.currentTarget.style.boxShadow = 'none'; }}
          />
          {fieldErrors.email && (
            <p style={{ fontSize: '12px', color: eff.error, margin: '4px 0 0' }}>{fieldErrors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="lead-phone" style={{ fontSize: '13px', fontWeight: 600, color: eff.text, display: 'block', marginBottom: '6px' }}>
            Phone <span style={{ fontWeight: 400, color: eff.textBody }}>(optional)</span>
            <HelpTip text="Email alone is enough. Adding your phone helps the business reach you faster if needed." />
          </label>
          <input
            id="lead-phone"
            type="tel"
            placeholder="(555) 123-4567"
            autoComplete="tel"
            value={leadData.phone}
            onChange={(e) => { updateLead('phone', e.target.value); setFieldErrors(p => ({ ...p, phone: undefined })); setError(null); }}
            style={{
              ...inputStyle,
              ...(fieldErrors.phone ? { borderColor: eff.error } : {}),
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = fieldErrors.phone ? eff.error : eff.buttonBg; e.currentTarget.style.boxShadow = `0 0 0 3px ${eff.buttonBorder}`; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = fieldErrors.phone ? eff.error : eff.buttonBorder; e.currentTarget.style.boxShadow = 'none'; }}
          />
          {fieldErrors.phone && (
            <p style={{ fontSize: '12px', color: eff.error, margin: '4px 0 0' }}>{fieldErrors.phone}</p>
          )}
          <SmsConsentDisclosure variant="inline" />
        </div>

        <div>
          <label htmlFor="lead-name" style={{ fontSize: '13px', fontWeight: 600, color: eff.text, display: 'block', marginBottom: '6px' }}>
            Name <span style={{ fontWeight: 400, color: eff.textBody }}>(optional)</span>
          </label>
          <input
            id="lead-name"
            type="text"
            placeholder="Your name"
            autoComplete="name"
            value={leadData.name}
            onChange={(e) => updateLead('name', e.target.value)}
            style={inputStyle}
            onFocus={(e) => { e.currentTarget.style.borderColor = eff.buttonBg; e.currentTarget.style.boxShadow = `0 0 0 3px ${eff.buttonBorder}`; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = eff.buttonBorder; e.currentTarget.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Custom fields (owner-defined) */}
        {customFields.map((f) => {
          const val = customValues[f.id];
          const err = customErrors[f.id];
          if (f.type === 'checkbox') {
            return (
              <div key={f.id}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '4px 0', cursor: 'pointer' }}>
                  <Checkbox
                    checked={val === true}
                    onCheckedChange={(c) => setCustomValue(f.id, !!c)}
                    className="h-5 w-5 rounded border-[#d5e1e7] data-[state=checked]:bg-[#394247] data-[state=checked]:border-[#394247] data-[state=checked]:text-[#e4edf1] focus-visible:ring-[#d5e1e7]"
                    style={{ marginTop: '2px' }}
                  />
                  <span style={{ fontSize: '13px', color: eff.textBody, lineHeight: 1.5 }}>
                    {f.label}{f.required ? ' *' : ''}
                  </span>
                </label>
                {err && <p style={{ fontSize: '12px', color: eff.error, margin: '4px 0 0' }}>{err}</p>}
              </div>
            );
          }
          return (
            <div key={f.id}>
              <label htmlFor={`cf-${f.id}`} style={{ fontSize: '13px', fontWeight: 600, color: eff.text, display: 'block', marginBottom: '6px' }}>
                {f.label}
                {!f.required && <span style={{ fontWeight: 400, color: eff.textBody }}> (optional)</span>}
              </label>
              {f.type === 'select' ? (
                <select
                  id={`cf-${f.id}`}
                  value={String(val ?? '')}
                  onChange={(e) => setCustomValue(f.id, e.target.value)}
                  style={{ ...inputStyle, ...(err ? { borderColor: eff.error } : {}) }}
                >
                  <option value="">Select...</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea
                  id={`cf-${f.id}`}
                  rows={3}
                  value={String(val ?? '')}
                  onChange={(e) => setCustomValue(f.id, e.target.value)}
                  style={{ ...inputStyle, resize: 'vertical', ...(err ? { borderColor: eff.error } : {}) }}
                />
              ) : (
                <input
                  id={`cf-${f.id}`}
                  type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text'}
                  value={String(val ?? '')}
                  onChange={(e) => setCustomValue(f.id, e.target.value)}
                  style={{ ...inputStyle, ...(err ? { borderColor: eff.error } : {}) }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = err ? eff.error : eff.buttonBg; e.currentTarget.style.boxShadow = `0 0 0 3px ${eff.buttonBorder}`; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = err ? eff.error : eff.buttonBorder; e.currentTarget.style.boxShadow = 'none'; }}
                />
              )}
              {err && <p style={{ fontSize: '12px', color: eff.error, margin: '4px 0 0' }}>{err}</p>}
            </div>
          );
        })}

        {/* SMS consent */}
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '4px 0',
          cursor: 'pointer',
        }}>
          <Checkbox
            checked={smsConsent}
            onCheckedChange={(checked) =>
              dispatch({ type: 'SET_SMS_CONSENT', value: !!checked })
            }
            className="h-5 w-5 rounded border-[#d5e1e7] data-[state=checked]:bg-[#394247] data-[state=checked]:border-[#394247] data-[state=checked]:text-[#e4edf1] focus-visible:ring-[#d5e1e7]"
            style={{ marginTop: '2px' }}
          />
          <span style={{ fontSize: '13px', color: eff.textBody, lineHeight: 1.5 }}>
            Send me text updates about my quote.
          </span>
        </label>

        {error && (
          <p style={{ fontSize: '14px', color: eff.error, margin: 0 }}>{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            ...primaryButtonStyle,
            opacity: submitting ? 0.6 : 1,
          }}
          onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = eff.buttonBgHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = eff.buttonBg; }}
          onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          {submitting ? (
            <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
          ) : (
            <Send style={{ width: 16, height: 16 }} />
          )}
          {config.calculator.cta_button_text || 'Send My Quote'}
        </button>

        {/* Trust line */}
        <p style={{
          fontSize: '12px',
          color: eff.textBody,
          textAlign: 'center',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
        }}>
          <Lock style={{ width: 11, height: 11, opacity: 0.6 }} />
          Your info goes to {config.calculator.business_name || 'this business'} (and is processed by WeFixTrades) to prepare your quote.
        </p>
      </form>
    </div>
  );
}
